'use client';

import { Link, usePage } from '@inertiajs/react';
import {
    m,
    AnimatePresence,
    useScroll,
    useSpring,
    useMotionValueEvent,
} from 'framer-motion';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';
import AppLogoIcon from '@/components/app-logo-icon';
import { Button } from '@/components/ui/button';
import { dashboard, login, register } from '@/routes';

const navLinks = [
    { href: '#about', label: 'Tentang' },
    { href: '#features', label: 'Fitur' },
    { href: '#guide', label: 'Cara Kerja' },
    { href: '#security', label: 'Keamanan' },
];

export default function LandingNav({
    name,
    logo,
}: {
    name: string;
    logo?: string;
}) {
    const { auth } = usePage().props;
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const { scrollY, scrollYProgress } = useScroll();
    const progressScaleX = useSpring(scrollYProgress, {
        stiffness: 120,
        damping: 30,
        restDelta: 0.001,
    });

    useMotionValueEvent(scrollY, 'change', (latest) => {
        setScrolled(latest > 16);
    });

    return (
        <>
            <m.nav
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className={`fixed top-0 z-50 w-full transition-all duration-300 ${
                    scrolled
                        ? 'border-b border-border/60 bg-background/80 backdrop-blur-xl'
                        : 'border-b border-transparent bg-transparent'
                }`}
            >
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8">
                    {/* Logo */}
                    <Link
                        href="/"
                        className="group flex min-w-0 items-center gap-2.5"
                    >
                        <div className="flex size-9 items-center justify-center overflow-hidden rounded-xl bg-primary text-white">
                            {logo ? (
                                <img
                                    src={logo}
                                    alt={name}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <AppLogoIcon className="size-5 text-white" />
                            )}
                        </div>
                        <span className="truncate text-base font-semibold tracking-tight text-foreground">
                            {name}
                        </span>
                    </Link>

                    {/* Desktop Nav Links */}
                    <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 md:flex">
                        {navLinks.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                {link.label}
                            </a>
                        ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex items-center gap-2">
                        {auth.user ? (
                            <Link href={dashboard.url()}>
                                <Button className="h-9 rounded-full px-5 text-sm font-medium">
                                    Dashboard
                                    <ArrowRight className="ml-1.5 size-4" />
                                </Button>
                            </Link>
                        ) : (
                            <>
                                <Link
                                    href={login.url()}
                                    className="hidden sm:block"
                                >
                                    <Button
                                        variant="ghost"
                                        className="h-9 rounded-full px-4 text-sm font-medium text-muted-foreground hover:text-foreground"
                                    >
                                        Masuk
                                    </Button>
                                </Link>
                                <Link
                                    href={register.url()}
                                    className="hidden sm:block"
                                >
                                    <Button className="h-9 rounded-full px-5 text-sm font-medium">
                                        Daftar
                                        <ArrowRight className="ml-1.5 size-4" />
                                    </Button>
                                </Link>
                            </>
                        )}

                        {/* Mobile hamburger */}
                        <button
                            className="flex size-9 items-center justify-center rounded-full border border-border/60 text-foreground md:hidden"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Menu"
                        >
                            {mobileOpen ? (
                                <X className="size-5" />
                            ) : (
                                <Menu className="size-5" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Scroll progress indicator */}
                <m.div
                    style={{ scaleX: progressScaleX }}
                    className={`absolute bottom-0 left-0 h-px w-full origin-left bg-gradient-to-r from-primary via-secondary to-[#ffd709] transition-opacity duration-300 ${
                        scrolled ? 'opacity-100' : 'opacity-0'
                    }`}
                />
            </m.nav>

            {/* Mobile Menu Drawer */}
            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <m.div
                            key="backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
                            onClick={() => setMobileOpen(false)}
                        />
                        <m.div
                            key="drawer"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{
                                type: 'spring',
                                stiffness: 300,
                                damping: 30,
                            }}
                            className="fixed top-0 right-0 z-50 flex h-full w-72 flex-col gap-6 border-l border-border/60 bg-background p-6 md:hidden"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold tracking-tight text-foreground">
                                    Menu
                                </span>
                                <button
                                    onClick={() => setMobileOpen(false)}
                                    className="flex size-8 items-center justify-center rounded-full bg-muted text-foreground"
                                    aria-label="Tutup"
                                >
                                    <X className="size-4" />
                                </button>
                            </div>
                            <nav className="flex flex-col gap-1">
                                {navLinks.map((link) => (
                                    <a
                                        key={link.href}
                                        href={link.href}
                                        onClick={() => setMobileOpen(false)}
                                        className="rounded-xl px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        {link.label}
                                    </a>
                                ))}
                            </nav>
                            <div className="mt-auto flex flex-col gap-2">
                                {!auth.user && (
                                    <Link
                                        href={login.url()}
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        <Button
                                            variant="outline"
                                            className="w-full rounded-full font-medium"
                                        >
                                            Masuk
                                        </Button>
                                    </Link>
                                )}
                                <Link
                                    href={
                                        auth.user
                                            ? dashboard.url()
                                            : register.url()
                                    }
                                    onClick={() => setMobileOpen(false)}
                                >
                                    <Button className="w-full rounded-full font-medium">
                                        {auth.user ? 'Dashboard' : 'Daftar'}
                                        <ArrowRight className="ml-1.5 size-4" />
                                    </Button>
                                </Link>
                            </div>
                        </m.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
